import { Link } from '@mui/material';
import {
  getGridDateOperators,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import moment from 'moment';

import { DATE_FORMAT, TIME_FORMAT } from '@/config';
import { formatPersonName } from '@/utils';

const unitName = (): Partial<GridColDef> => ({
  valueGetter: (_, row) => row?.unitShift?.unit?.name || '-',
});

const unitType = (): Partial<GridColDef> => ({
  valueGetter: (_, row) => row?.unitShift?.unit?.type?.name || '-',
});

const assignmentStatus = (): Partial<GridColDef> => ({
  valueGetter: (_, row) => row?.status || '-',
});

const cfsNumber = (): Partial<GridColDef> => ({
  field: 'cfsNumber',
  renderCell: (params: GridRenderCellParams) => {
    const number = params.row?.cfsNumber;
    const cfsId = params.row?.cfsId;

    return (
      <Link
        href={`/cfs/${cfsId}`}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{ cursor: 'pointer', color: 'red' }}
        onClick={(e) => e.stopPropagation()}
      >
        {number}
      </Link>
    );
  },
  valueGetter: (_, row) => {
    return row.cfsNumber || '-';
  },
});

const unitPersons = (): Partial<GridColDef> => ({
  field: 'unitPersons',
  renderCell: (params: GridRenderCellParams) => {
    const users = params.row?.unitShift?.users || [];

    if (!users.length) return 'No users assigned';

    const userNames = users
      .map((user: any) => formatPersonName(user))
      .join(', ');

    return userNames;
  },
  valueGetter: (_, row) => {
    const users = row.unitShift?.users || [];
    if (!users.length) return 'No users assigned';
    return users.map((user: any) => formatPersonName(user)).join(', ');
  },
});

const unitVehicles = (): Partial<GridColDef> => ({
  field: 'unitVehicles',
  renderCell: (params: GridRenderCellParams) => {
    const vehicles = params.row?.unitShift?.vehicles || [];

    if (!vehicles.length) return 'No vehicles assigned';

    const vehicleNames = vehicles
      .map((vehicle: any) => vehicle.name)
      .join(', ');

    return vehicleNames;
  },
  valueGetter: (_, row) => {
    const vehicles = row.unitShift?.vehicles || [];
    if (!vehicles.length) return 'No vehicles assigned';
    return vehicles.map((vehicle: any) => vehicle.name).join(', ');
  },
});

const unitEquipment = (): Partial<GridColDef> => ({
  field: 'unitEquipment',
  renderCell: (params: GridRenderCellParams) => {
    const equipments = params.row?.unitShift?.equipments || [];

    if (!equipments.length) return 'No equipment assigned';

    const equipmentNames = equipments
      .map((equipment: any) => equipment.name)
      .join(', ');

    return equipmentNames;
  },
  valueGetter: (_, row) => {
    const equipments = row.unitShift?.equipments || [];
    if (!equipments.length) return 'No equipment assigned';
    return equipments.map((equipment: any) => equipment.name).join(', ');
  },
});

const createdAt = (): Partial<GridColDef> => ({
  valueGetter: (_, row) => {
    return row?.unitShift?.startAt
      ? `${moment(row.unitShift.startAt).format(DATE_FORMAT)} • ${moment(
          row.unitShift.startAt,
        ).format(TIME_FORMAT)}`
      : '-';
  },
  filterOperators: getGridDateOperators(true),
});

const closedAt = (): Partial<GridColDef> => ({
  valueGetter: (_, row) => {
    return row?.unitShift?.endAt
      ? `${moment(row.unitShift.endAt).format(DATE_FORMAT)} • ${moment(
          row.unitShift.endAt,
        ).format(TIME_FORMAT)}`
      : '-';
  },
  filterOperators: getGridDateOperators(true),
});

export const unitShiftsCols = () => {
  return {
    unitName,
    unitType,
    assignmentStatus,
    cfsNumber,
    unitPersons,
    unitVehicles,
    unitEquipment,
    createdAt,
    closedAt,
  };
};
